import { useLazyQuery } from "@apollo/client";
import { useEffect, useState } from "react";
import { useUser } from "../../classes/contexts/UserContext";
import { formatDateWithTime } from "../../classes/utils/DateUtils";
import {
  calculateBMI,
  convertHeight,
  convertWeight,
} from "../../classes/utils/HealthUtils";
import { GET_HEIGHTS_BY_IDS } from "../../queries/heightQueries";
import { GET_PATIENT_CONDITIONS_BY_IDS } from "../../queries/patientConditionQueries";
import { GET_PATIENT_NOTES_BY_IDS } from "../../queries/patientNoteQueries";
import { GET_WEIGHTS_BY_IDS } from "../../queries/weightQueries";
import { GET_QUALITY_OF_LIFE_INDICATORS_BY_IDS } from "../../queries/qualityOfLifeIndicatorQueries";
import "./ClinicalHistoryViewer.css";

const ClinicalHistoryViewer = ({ history, className = "" }) => {
  const { userPreferences } = useUser();
  const [weightValue, setWeightValue] = useState(null);
  const [heightValue, setHeightValue] = useState(null);
  const [conditionsList, setConditionsList] = useState([]);
  const [noteContent, setNoteContent] = useState("");
  const [qualityOfLifeIndicators, setQualityOfLifeIndicators] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Get user's preferred units (default to kg and m if not set)
  const weightUnit =
    userPreferences?.display_settings?.weight_unit?.toLowerCase() || "kg";
  const heightUnit =
    userPreferences?.display_settings?.length_unit?.toLowerCase() || "m";

  // Lazy queries to fetch referenced documents
  const [getWeights] = useLazyQuery(GET_WEIGHTS_BY_IDS);
  const [getHeights] = useLazyQuery(GET_HEIGHTS_BY_IDS);
  const [getNotes] = useLazyQuery(GET_PATIENT_NOTES_BY_IDS);
  const [getConditions] = useLazyQuery(GET_PATIENT_CONDITIONS_BY_IDS);
  const [getQualityIndicators] = useLazyQuery(
    GET_QUALITY_OF_LIFE_INDICATORS_BY_IDS
  );

  // Fetch all related data when history changes
  useEffect(() => {
    const fetchData = async () => {
      if (!history) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);

      try {
        setQualityOfLifeIndicators([]);

        // Fetch weight
        if (history.weight) {
          const { data: weightData } = await getWeights({
            variables: { ids: [history.weight] },
          });
          if (weightData?.weightsByIds?.[0]) {
            const weightDoc = weightData.weightsByIds[0];
            const converted = convertWeight(
              parseFloat(weightDoc.value),
              "kg",
              weightUnit
            );
            setWeightValue(converted.toFixed(1));
          }
        }

        // Fetch height
        if (history.height) {
          const { data: heightData } = await getHeights({
            variables: { ids: [history.height] },
          });
          if (heightData?.heightsByIds?.[0]) {
            const heightDoc = heightData.heightsByIds[0];
            const converted = convertHeight(
              parseFloat(heightDoc.value),
              "m",
              heightUnit
            );
            setHeightValue(
              heightUnit === "m" ? converted.toFixed(2) : converted.toFixed(1)
            );
          }
        }

        // Fetch conditions
        if (history.conditions) {
          const { data: conditionsData } = await getConditions({
            variables: { ids: [history.conditions] },
          });
          if (conditionsData?.patientConditionsByIds?.[0]) {
            setConditionsList(
              conditionsData.patientConditionsByIds[0].conditions || []
            );
          }
        }

        // Fetch note
        if (history.note) {
          const { data: noteData } = await getNotes({
            variables: { ids: [history.note] },
          });
          if (noteData?.patientNotesByIds?.[0]) {
            setNoteContent(noteData.patientNotesByIds[0].content || "");
          }
        }

        // Fetch Quality of Life indicators (standard + custom)
        const qosIds = [
          ...(history.quality_of_life_indicators || []),
          ...(history.custom_quality_of_life_indicators || []),
        ].filter(Boolean);

        if (qosIds.length > 0) {
          const { data: qolData } = await getQualityIndicators({
            variables: { ids: qosIds },
          });
          const parsed =
            qolData?.qualityOfLifeIndicatorsByIds?.map(item => ({
              id: item._id,
              name: item.name,
              value: item.value,
              max_value: item.max_value,
              date: item.date || item.createdAt,
              is_custom: item.is_custom,
            })) || [];
          setQualityOfLifeIndicators(parsed);
        }
      } catch (error) {
        console.error("Error fetching clinical history data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [
    history,
    weightUnit,
    heightUnit,
    getWeights,
    getHeights,
    getNotes,
    getConditions,
    getQualityIndicators,
  ]);

  // Calculate BMI
  const bmi = (() => {
    if (!weightValue || !heightValue) return "N/A";
    const weightInKg = convertWeight(parseFloat(weightValue), weightUnit, "kg");
    const heightInM = convertHeight(parseFloat(heightValue), heightUnit, "m");
    return calculateBMI(weightInKg, heightInM);
  })();

  if (!history) {
    return (
      <div className={`clinical-history-viewer ${className}`}>
        <div className="clinical-history-viewer-error">
          No clinical history data available
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={`clinical-history-viewer ${className}`}>
        <div className="clinical-history-viewer-loading">Loading...</div>
      </div>
    );
  }

  const formattedDate = formatDateWithTime(history.createdAt);

  return (
    <div className={`clinical-history-viewer ${className}`}>
      <div className="clinical-history-viewer-container">
        <div className="clinical-history-viewer-header">
          <span className="clinical-history-viewer-date">{formattedDate}</span>
        </div>

        <div className="clinical-history-viewer-content">
          <div className="clinical-history-viewer-metrics">
            <div className="clinical-history-viewer-metric">
              <span className="metric-label">Weight</span>
              <span className="metric-value">
                {weightValue ? `${weightValue} ${weightUnit}` : "N/A"}
              </span>
            </div>
            <div className="clinical-history-viewer-metric">
              <span className="metric-label">Height</span>
              <span className="metric-value">
                {heightValue ? `${heightValue} ${heightUnit}` : "N/A"}
              </span>
            </div>
            <div className="clinical-history-viewer-metric">
              <span className="metric-label">BMI</span>
              <span className="metric-value">{bmi}</span>
            </div>
          </div>

          {conditionsList.length > 0 && (
            <div className="clinical-history-viewer-conditions">
              <span className="conditions-label">Conditions</span>
              <div className="conditions-list">
                {conditionsList.map((condition, index) => (
                  <span key={index} className="condition-tag">
                    {condition}
                  </span>
                ))}
              </div>
            </div>
          )}

          {noteContent && (
            <div className="clinical-history-viewer-notes">
              <span className="notes-label">Notes</span>
              <p className="notes-content">{noteContent}</p>
            </div>
          )}

          {qualityOfLifeIndicators.length > 0 && (
            <div className="clinical-history-viewer-qol">
              <div className="qol-title">Quality of Life</div>
              <div className="qol-list">
                {qualityOfLifeIndicators.map(item => (
                  <div key={item.id} className="qol-item">
                    <div className="qol-row">
                      <span className="qol-label">
                        {(item.name || "QOL").slice(0, 5)}
                      </span>
                      <span className="qol-value">
                        <span className="qol-current">
                          {item.value ?? "N/A"}
                        </span>
                        {item.max_value ? (
                          <>
                            <span className="qol-separator">/</span>
                            <span className="qol-max">{item.max_value}</span>
                          </>
                        ) : null}
                      </span>
                    </div>
                    <div className="qol-date">
                      {item.date ? formatDateWithTime(item.date) : ""}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClinicalHistoryViewer;
